package app.alefly.domain.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * Status de disponibilidade de um Tenant na plataforma.
 */
@Serializable
enum class TenantStatus {
    /** Tenant ativo e disponível para os usuários. */
    @SerialName("active")
    ACTIVE,

    /** Tenant desativado e oculto na plataforma. */
    @SerialName("disabled")
    DISABLED,
}

/**
 * Níveis de dificuldade suportados pelos quizzes.
 */
@Serializable
enum class Difficulty {
    /** Nível fácil, ideal para iniciantes. */
    @SerialName("easy")
    EASY,

    /** Nível intermediário, exige conhecimento moderado. */
    @SerialName("medium")
    MEDIUM,

    /** Nível difícil, para especialistas no tema. */
    @SerialName("hard")
    HARD,

    /** Seleção aleatória de dificuldade. */
    @SerialName("random")
    RANDOM,
}

@Serializable
enum class HintPenaltyType {
    @SerialName("score")
    SCORE,
}

/**
 * Informações de branding e textos de apresentação do Tenant.
 */
@Serializable
data class TenantBranding(
    val displayName: String,
    val tagline: String,
    val description: String,
)

/**
 * Cores customizadas para o tema visual do Tenant.
 * Permite que cada tenant tenha uma identidade visual única (ex: cores do clube ou do tema).
 */
@Serializable
data class TenantVisualTheme(
    val lobbyBgStart: String? = null,
    val lobbyBgEnd: String? = null,
    val gameBgStart: String? = null,
    val gameBgEnd: String? = null,
    val surfaceBg: String? = null,
    val surfaceBorder: String? = null,
    val hudBg: String? = null,
    val hudBorder: String? = null,
    val textColor: String? = null,
    val softTextColor: String? = null,
    val correctColor: String? = null,
    val incorrectColor: String? = null,
)

@Serializable
data class TenantVisual(
    val primaryColor: String,
    val accentColor: String,
    val theme: TenantVisualTheme = TenantVisualTheme(),
)

@Serializable
data class TenantExperienceMetric(
    val id: String,
    val label: String,
)

@Serializable
data class TenantExperienceCard(
    val eyebrow: String,
    val title: String,
    val body: String,
)

/**
 * Configuração da experiência de usuário e conteúdo informativo do Tenant.
 */
@Serializable
data class TenantExperience(
    val modeLabel: String,
    val supportCopy: String,
    val stageLabel: String,
    val stageTitle: String,
    val stageSteps: List<String>,
    val pulseLabel: String,
    val pulseCaption: String,
    val metrics: List<TenantExperienceMetric>,
    val cards: List<TenantExperienceCard>,
    val missionTitle: String,
    val missionCopy: String,
    val missions: List<TenantExperienceCard>,
)

/**
 * Regras e limites do quiz para o Tenant.
 */
@Serializable
data class TenantQuizConfig(
    val minQuestions: Int,
    val maxQuestions: Int,
    val defaultQuestions: Int,
    val difficultiesEnabled: Boolean,
    val supportedDifficulties: List<Difficulty>,
    val defaultDifficulty: Difficulty,
    val hintsEnabled: Boolean = true,
    val explanationsEnabled: Boolean = true,
    val answerExpirationSeconds: Int = 30,
    val maxPointsPerQuestion: Int = 100,
    val minPointsPerQuestion: Int = 10,
)

/**
 * Configuração de anúncios para as diferentes plataformas (Web, Android, iOS).
 */
@Serializable
data class TenantAds(
    val enabled: Boolean,
    val childDirected: Boolean = false,
    val android: TenantAdsMobilePlatform? = null,
    val ios: TenantAdsMobilePlatform? = null,
    val web: TenantAdsWeb? = null,
) {
    /** Banner habilitado para a plataforma se ads estão ativos e há bannerId configurado. */
    val androidBannerEnabled: Boolean get() = enabled && android?.bannerId != null
    val androidRewardedEnabled: Boolean get() = enabled && android?.rewardedId != null
    val iosBannerEnabled: Boolean get() = enabled && ios?.bannerId != null
    val iosRewardedEnabled: Boolean get() = enabled && ios?.rewardedId != null
    val webBannerEnabled: Boolean get() = enabled && web?.bannerSlotId != null
    val webRewardedEnabled: Boolean get() = enabled && web?.rewardedSlotId != null
}

@Serializable
data class TenantAdsMobilePlatform(
    val bannerId: String? = null,
    val rewardedId: String? = null,
)

@Serializable
data class TenantAdsWeb(
    val publisherId: String? = null,
    val bannerSlotId: String? = null,
    val rewardedSlotId: String? = null,
)

/**
 * Metadados de SEO para indexação e busca.
 */
@Serializable
data class TenantSeo(
    val metaTitle: String,
    val metaDescription: String,
    val keywords: List<String>,
)

/**
 * Configuração comercial para exibir CTAs ou ofertas dentro do Tenant.
 */
@Serializable
data class TenantCommercial(
    val enabled: Boolean,
    val eyebrow: String,
    val title: String,
    val body: String,
    val audienceTags: List<String>,
    val ctaLabel: String,
    val ctaHref: String,
)

@Serializable
data class TenantLegal(
    val requiresDisclaimer: Boolean,
    val disclaimerText: String,
)

/**
 * Identificadores de publicação do Tenant nas lojas oficiais de aplicativos (Google Play e Apple App Store).
 */
@Serializable
data class TenantStoreLinks(
    val androidPackageName: String? = null,
    val iosAppId: String? = null,
) {
    val hasAnyStore: Boolean get() = !androidPackageName.isNullOrBlank() || !iosAppId.isNullOrBlank()
}

/**
 * Representa um "Inquilino" (Tenant) da plataforma.
 * Cada tenant é uma instância isolada com seu próprio conteúdo (quizzes), branding e regras.
 */
@Serializable
data class Tenant(
    val id: TenantId,
    val status: TenantStatus,
    val comingSoon: Boolean = false,
    val supportedLocales: List<String>,
    val defaultLocale: String,
    val branding: TenantBranding,
    val visual: TenantVisual,
    val experience: TenantExperience,
    val quiz: TenantQuizConfig,
    val ads: TenantAds,
    val seo: TenantSeo,
    val commercial: TenantCommercial = TenantCommercial(
        enabled = false,
        eyebrow = "",
        title = "",
        body = "",
        audienceTags = emptyList(),
        ctaLabel = "",
        ctaHref = "",
    ),
    val legal: TenantLegal,
    val categories: List<String> = emptyList(),
    val storeLinks: TenantStoreLinks? = null,
    val createdAt: Long? = null,
    val updatedAt: Long? = null,
)
